"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ProjectFileCollectionValidator = exports.TYPE_DETECTION_EXCLUDED_SEMANTIC_TYPES = exports.TYPE_DETECTION_SEMANTIC_TYPES = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const sigma_engine_shared_2 = require("@cadstrom/sigma-engine-shared");
const required_files_1 = require("./required-files");
const validation_1 = require("./validation");
const FILE_VALIDATION_CONCURRENCY = 20;
exports.TYPE_DETECTION_SEMANTIC_TYPES = new Set([
    'project',
    'board',
    'schematic',
]);
exports.TYPE_DETECTION_EXCLUDED_SEMANTIC_TYPES = new Set(['netlist']);
function toForwardSlash(p) {
    return p.replace(/\\/g, '/');
}
function normalizeForComparison(p) {
    return sigma_engine_shared_1.Files.normalizePath(toForwardSlash(p)).toLowerCase();
}
async function settleWithConcurrency(tasks, concurrency) {
    const results = new Array(tasks.length);
    let next = 0;
    async function consume() {
        while (next < tasks.length) {
            const i = next++;
            try {
                results[i] = { status: 'fulfilled', value: await tasks[i]() };
            }
            catch (reason) {
                results[i] = { status: 'rejected', reason };
            }
        }
    }
    await Promise.all(Array.from({ length: Math.min(concurrency, tasks.length) }, () => consume()));
    return results;
}
function buildCacheKey(file) {
    const f = file.contents;
    const lastModified = typeof f.lastModified === 'number' ? f.lastModified : 0;
    return `${file.relativePath}|${file.contents.size}|${lastModified}`;
}
class ProjectFileCollectionValidator {
    constructor() {
        this.fileValidationCache = new Map();
    }
    async validate(files, options = {}) {
        const innerResults = [];
        this.validateMetadata(files, innerResults);
        const fileDetails = await this.validateFileContents(files, innerResults, options.declaredTool);
        const projectType = this.detectProjectType(fileDetails, innerResults, options.declaredTool);
        const missingRefs = this.checkReferencedFiles(files, fileDetails, innerResults);
        const requiredFileResults = this.checkRequiredFiles(projectType, files, fileDetails, missingRefs, innerResults);
        const detected = projectType ? this.detectProjects(projectType, files, fileDetails, innerResults) : undefined;
        const summary = this.buildSummary(fileDetails, missingRefs, requiredFileResults);
        const result = {
            type: 'file-collection-validation',
            message: 'File collection validation complete',
            innerResults,
            projectType,
            projectDefinition: detected?.projectDefinition,
            projects: detected?.projects,
            summary,
        };
        (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(result);
        return result;
    }
    validateMetadata(files, innerResults) {
        if (files.length > sigma_engine_shared_1.Files.UPLOAD_LIMITS.MAX_FILES) {
            innerResults.push({
                type: 'metadata-validation',
                message: `Too many files: ${files.length} exceeds maximum of ${sigma_engine_shared_1.Files.UPLOAD_LIMITS.MAX_FILES}`,
                severity: sigma_engine_shared_1.BoundaryLevel.Error,
                relativePath: '',
                errorType: sigma_engine_shared_2.FileCollectionValidationErrorType.TooManyFiles,
            });
        }
        const totalSize = files.reduce((sum, f) => sum + f.contents.size, 0);
        if (totalSize > sigma_engine_shared_1.Files.UPLOAD_LIMITS.MAX_TOTAL_BYTES) {
            innerResults.push({
                type: 'metadata-validation',
                message: `Total size ${totalSize} bytes exceeds maximum of ${sigma_engine_shared_1.Files.UPLOAD_LIMITS.MAX_TOTAL_BYTES / (1024 * 1024 * 1024)} GB`,
                severity: sigma_engine_shared_1.BoundaryLevel.Error,
                relativePath: '',
                errorType: sigma_engine_shared_2.FileCollectionValidationErrorType.TotalSizeExceeded,
            });
        }
        for (const file of files) {
            const errors = sigma_engine_shared_1.Files.validateFileMetadata({
                relativePath: file.relativePath,
                size: file.contents.size,
            });
            for (const error of errors) {
                const errorType = this.mapValidationErrorCode(error.code);
                innerResults.push({
                    type: 'metadata-validation',
                    message: error.message,
                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
                    relativePath: file.relativePath,
                    errorType,
                });
            }
        }
    }
    mapValidationErrorCode(code) {
        switch (code) {
            case 'FILE_TOO_LARGE':
                return sigma_engine_shared_2.FileCollectionValidationErrorType.FileTooLarge;
            case 'PATH_TRAVERSAL':
                return sigma_engine_shared_2.FileCollectionValidationErrorType.PathTraversal;
            case 'PATH_TOO_DEEP':
                return sigma_engine_shared_2.FileCollectionValidationErrorType.PathTooDeep;
            case 'PATH_TOO_LONG':
                return sigma_engine_shared_2.FileCollectionValidationErrorType.PathTooLong;
            case 'INVALID_FILENAME':
                return sigma_engine_shared_2.FileCollectionValidationErrorType.InvalidFilename;
            case 'BLOCKED_EXTENSION':
                return sigma_engine_shared_2.FileCollectionValidationErrorType.BlockedExtension;
            default:
                return sigma_engine_shared_2.FileCollectionValidationErrorType.InvalidFilename;
        }
    }
    async validateFileContents(files, innerResults, declaredTool) {
        const currentKeys = new Set();
        const fileKeyPairs = files.map((file) => {
            const key = buildCacheKey(file);
            currentKeys.add(key);
            return { file, key };
        });
        for (const existingKey of this.fileValidationCache.keys()) {
            if (!currentKeys.has(existingKey)) {
                this.fileValidationCache.delete(existingKey);
            }
        }
        const settled = await settleWithConcurrency(fileKeyPairs.map(({ file, key }) => async () => {
            const cached = this.fileValidationCache.get(key);
            if (cached) {
                return { file, result: cached };
            }
            const result = await (0, validation_1.validateFile)(sigma_engine_shared_1.Files.basenamePath(toForwardSlash(file.relativePath)), file.contents);
            this.fileValidationCache.set(key, result);
            return { file, result };
        }), FILE_VALIDATION_CONCURRENCY);
        const fileDetails = [];
        for (const outcome of settled) {
            if (outcome.status === 'rejected') {
                fileDetails.push({
                    type: 'file-validation',
                    message: `Validation failed: ${outcome.reason instanceof Error ? outcome.reason.message : String(outcome.reason)}`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
                    relativePath: '',
                    size: 0,
                });
                continue;
            }
            const { file, result } = outcome.value;
            const detail = {
                type: 'file-validation',
                relativePath: file.relativePath,
                size: file.contents.size,
                fileValidationResult: result,
                message: '',
                severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
            };
            if (result.isValid) {
                detail.semanticType = result.data.semanticType;
                detail.cadTool = result.data.fileFormat.cadTool;
                detail.referencedFiles = result.data.referencedFiles;
                detail.message = `${sigma_engine_shared_1.Files.basenamePath(toForwardSlash(file.relativePath))}: ${result.data.semanticType} (${result.data.fileFormat.cadTool})`;
            }
            else {
                const isUnrecognized = result.errors.some((e) => e.startsWith('Unrecognized file type'));
                detail.semanticType = result.semanticType;
                const isForeignToDeclaredTool = declaredTool !== undefined &&
                    result.claimedTools !== undefined &&
                    result.claimedTools.length > 0 &&
                    !result.claimedTools.includes(declaredTool);
                detail.message = result.errors.join('; ');
                if (isUnrecognized) {
                    detail.severity = sigma_engine_shared_1.BoundaryLevel.Warning;
                    detail.errorType = sigma_engine_shared_2.FileCollectionValidationErrorType.UnrecognizedFileType;
                }
                else if (isForeignToDeclaredTool) {
                    detail.severity = sigma_engine_shared_1.BoundaryLevel.Warning;
                    detail.errorType = sigma_engine_shared_2.FileCollectionValidationErrorType.ForeignToolFile;
                    detail.message = `${detail.message} (not part of the declared ${declaredTool} project)`;
                }
                else {
                    detail.severity = sigma_engine_shared_1.BoundaryLevel.Error;
                    detail.errorType = sigma_engine_shared_2.FileCollectionValidationErrorType.FileValidationFailed;
                }
            }
            fileDetails.push(detail);
        }
        innerResults.push(...fileDetails);
        return fileDetails;
    }
    detectProjectType(fileDetails, innerResults, declaredTool) {
        const qualifyingTools = new Set();
        let hasExcludedNetlists = false;
        for (const detail of fileDetails) {
            if (!detail.semanticType || !detail.cadTool) {
                continue;
            }
            if (exports.TYPE_DETECTION_EXCLUDED_SEMANTIC_TYPES.has(detail.semanticType)) {
                hasExcludedNetlists = true;
                continue;
            }
            if (exports.TYPE_DETECTION_SEMANTIC_TYPES.has(detail.semanticType)) {
                qualifyingTools.add(detail.cadTool);
            }
        }
        const detectedTypes = Array.from(qualifyingTools);
        if (detectedTypes.length === 0) {
            innerResults.push({
                type: 'project-type-detection',
                message: 'No recognized project type could be determined. No files were uploaded.',
                severity: sigma_engine_shared_1.BoundaryLevel.Error,
                detectedTypes: [],
            });
            return undefined;
        }
        let selectedType;
        if (detectedTypes.length > 1) {
            if (declaredTool === undefined || !qualifyingTools.has(declaredTool)) {
                innerResults.push({
                    type: 'project-type-detection',
                    message: `Multiple project types detected: ${detectedTypes.join(', ')}. Please upload files from a single CAD tool.`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
                    detectedTypes,
                });
                return undefined;
            }
            selectedType = declaredTool;
            innerResults.push({
                type: 'project-type-detection',
                message: `Multiple project types detected (${detectedTypes.join(', ')}). Using the declared project type: ${declaredTool}.`,
                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                detectedTypes,
                selectedType,
            });
        }
        else {
            selectedType = detectedTypes[0];
            innerResults.push({
                type: 'project-type-detection',
                message: `Detected project type: ${selectedType}`,
                severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
                detectedTypes,
                selectedType,
            });
        }
        if (hasExcludedNetlists) {
            innerResults.push({
                type: 'project-type-detection',
                message: 'Netlist files were present but excluded from project type detection (netlists can originate from any CAD tool)',
                severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
                detectedTypes: [],
            });
        }
        return selectedType;
    }
    checkReferencedFiles(files, fileDetails, innerResults) {
        const normalizedPaths = new Set(files.map((f) => normalizeForComparison(f.relativePath)));
        const normalizedBasenames = new Set(files.map((f) => sigma_engine_shared_1.Files.basenamePath(normalizeForComparison(f.relativePath))));
        const missingFiles = [];
        for (const detail of fileDetails) {
            if (!detail.referencedFiles?.length) {
                continue;
            }
            const fileDir = sigma_engine_shared_1.Files.dirnamePath(normalizeForComparison(detail.relativePath));
            for (const ref of detail.referencedFiles) {
                const normalizedRef = normalizeForComparison(ref);
                const refBasename = sigma_engine_shared_1.Files.basenamePath(normalizedRef);
                if (normalizedPaths.has(normalizedRef)) {
                    continue;
                }
                const resolved = normalizeForComparison(sigma_engine_shared_1.Files.joinPath(fileDir, normalizedRef));
                if (normalizedPaths.has(resolved)) {
                    continue;
                }
                if (normalizedBasenames.has(refBasename)) {
                    continue;
                }
                if (!missingFiles.includes(ref)) {
                    missingFiles.push(ref);
                }
                innerResults.push({
                    type: 'referenced-file-check',
                    message: `File "${detail.relativePath}" references "${ref}" which is not in the uploaded files`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                    referencingFile: detail.relativePath,
                    missingFile: ref,
                });
            }
        }
        return missingFiles;
    }
    checkRequiredFiles(projectType, files, fileDetails, missingReferencedFiles, innerResults) {
        if (!projectType) {
            return [];
        }
        const results = (0, required_files_1.checkRequiredFilesForTool)(projectType, { files, fileDetails, missingReferencedFiles });
        innerResults.push(...results);
        return results;
    }
    detectProjects(projectType, files, fileDetails, innerResults) {
        const detection = (0, validation_1.detectProjectsForTool)(projectType, files, fileDetails);
        for (const warning of detection.warnings) {
            innerResults.push({
                type: 'project-type-detection',
                message: warning,
                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                detectedTypes: [projectType],
                selectedType: projectType,
            });
        }
        if (detection.projects.length === 0) {
            innerResults.push({
                type: 'project-type-detection',
                message: `No ${projectType} project could be assembled from the uploaded files.`,
                severity: sigma_engine_shared_1.BoundaryLevel.Error,
                detectedTypes: [projectType],
                errorType: sigma_engine_shared_2.FileCollectionValidationErrorType.MultipleProjectsDetected,
            });
            return { projectDefinition: undefined, projects: detection.projects };
        }
        if (detection.projects.length > 1) {
            const projectNames = detection.projects.map((p) => p.name ?? '(unnamed)');
            innerResults.push({
                type: 'project-type-detection',
                message: `Multiple ${projectType} projects detected (${projectNames.join(', ')}). Please upload files from a single project.`,
                severity: sigma_engine_shared_1.BoundaryLevel.Error,
                detectedTypes: [projectType],
                errorType: sigma_engine_shared_2.FileCollectionValidationErrorType.MultipleProjectsDetected,
            });
            return { projectDefinition: undefined, projects: detection.projects };
        }
        return { projectDefinition: detection.projects[0], projects: detection.projects };
    }
    buildSummary(fileDetails, missingRefs, requiredFileResults) {
        let validFiles = 0;
        let invalidFiles = 0;
        let boards = 0;
        let schematics = 0;
        let projectFiles = 0;
        let otherRecognized = 0;
        for (const detail of fileDetails) {
            if (detail.severity === sigma_engine_shared_1.BoundaryLevel.Error) {
                invalidFiles++;
            }
            else {
                validFiles++;
                switch (detail.semanticType) {
                    case 'board':
                        boards++;
                        break;
                    case 'schematic':
                        schematics++;
                        break;
                    case 'project':
                        projectFiles++;
                        break;
                    default:
                        if (detail.semanticType) {
                            otherRecognized++;
                        }
                        break;
                }
            }
        }
        return {
            totalFiles: fileDetails.length,
            validFiles,
            invalidFiles,
            boards,
            schematics,
            projectFiles,
            otherRecognized,
            missingReferencedFiles: missingRefs,
            missingRequiredFiles: [
                ...new Set(requiredFileResults
                    .filter((r) => r.type === 'required-file-check' && r.severity === sigma_engine_shared_1.BoundaryLevel.Error)
                    .map((r) => r.requiredFileExt)),
            ],
        };
    }
}
exports.ProjectFileCollectionValidator = ProjectFileCollectionValidator;
//# sourceMappingURL=ProjectFileCollectionValidator.js.map